import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { CheckCircle, Loader2, PartyPopper, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  async function pollPaymentStatus() {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      setPaymentDetails(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  }

  return (
    <Layout>
      <section className="section-padding bg-slate-50 min-h-screen flex items-center" data-testid="checkout-success">
        <div className="container-custom">
          <div className="max-w-lg mx-auto text-center">
            {status === 'loading' && (
              <div className="bg-white rounded-2xl p-10 border border-slate-200">
                <Loader2 className="w-16 h-16 text-copper animate-spin mx-auto mb-6" />
                <h1 className="font-heading text-2xl font-bold text-navy mb-3">
                  Processing Your Payment
                </h1>
                <p className="text-slate-600">
                  Please wait while we confirm your payment...
                </p>
              </div>
            )}

            {status === 'success' && (
              <div className="bg-white rounded-2xl p-10 border border-slate-200">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <PartyPopper className="w-6 h-6 text-copper" />
                  <span className="text-copper font-semibold">Payment Successful!</span>
                </div>
                <h1 className="font-heading text-2xl font-bold text-navy mb-3">
                  Welcome to BevRelay!
                </h1>
                <p className="text-slate-600 mb-6">
                  {paymentDetails?.package_id === 'brand_builder' 
                    ? 'You now have full access to Brand Builder and AI Blueprint tools.'
                    : 'Your coalition membership is now active. Start connecting with buyers today.'}
                </p>
                
                {paymentDetails && (
                  <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                    <div className="text-sm text-slate-500 mb-2">Order Details</div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Package</span>
                      <span className="font-medium text-navy">
                        {paymentDetails.metadata?.package_name || 'BevRelay Access'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-slate-600">Amount</span>
                      <span className="font-medium text-navy">
                        ${paymentDetails.amount_total?.toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {paymentDetails?.package_id === 'brand_builder' ? (
                    <>
                      <Link
                        to="/ai-blueprint"
                        className="w-full btn-copper flex items-center justify-center gap-2"
                        data-testid="go-to-blueprint-btn"
                      >
                        Try AI Blueprint Builder
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                      <Link
                        to="/build-brand"
                        className="w-full btn-primary flex items-center justify-center gap-2"
                      >
                        Open Brand Builder
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/dashboard"
                        className="w-full btn-copper flex items-center justify-center gap-2"
                        data-testid="go-to-dashboard-btn"
                      >
                        Go to Dashboard
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                      <Link
                        to="/target-list"
                        className="w-full btn-primary flex items-center justify-center gap-2"
                      >
                        Browse Target List
                      </Link>
                    </>
                  )}
                </div>
              </div>
            )}

            {status === 'timeout' && (
              <div className="bg-white rounded-2xl p-10 border border-slate-200">
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-yellow-600" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-navy mb-3">
                  Payment Processing
                </h1>
                <p className="text-slate-600 mb-6">
                  Your payment is being processed. You'll receive a confirmation email shortly.
                  If you don't receive it within a few minutes, please contact support.
                </p>
                <Link to="/" className="btn-primary">
                  Return to Home
                </Link>
              </div>
            )}

            {status === 'expired' && (
              <div className="bg-white rounded-2xl p-10 border border-slate-200">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">⏰</span>
                </div>
                <h1 className="font-heading text-2xl font-bold text-navy mb-3">
                  Session Expired
                </h1>
                <p className="text-slate-600 mb-6">
                  Your checkout session has expired. Please try again.
                </p>
                <Link to="/checkout" className="btn-copper">
                  Try Again
                </Link>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-white rounded-2xl p-10 border border-slate-200">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">❌</span>
                </div>
                <h1 className="font-heading text-2xl font-bold text-navy mb-3">
                  Something Went Wrong
                </h1>
                <p className="text-slate-600 mb-6">
                  We couldn't verify your payment. Please contact support if you were charged.
                </p>
                <Link to="/" className="btn-primary">
                  Return to Home
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
